/**
 * =============================================================================
 * SCRIPT-NAME:   gsc_wochenbericht.js
 * PROJEKT:       Freiseinapp — GSC KPI
 * MODUL:         Der GSC-Wochenbericht aus der Datenbank
 * BESCHREIBUNG:  Liest gsc_get_wochenbericht.php. Zeigt, was der Markdown-Bericht
 *                zeigt — PLUS den Verlauf je Seite über alle Wochen, und das kann
 *                die Datei nicht.
 *
 *                🔴 Reihenfolge ist Wirkung je Aufwand: Rang 1 sind die Seiten,
 *                die schon auf Seite 1 stehen und trotzdem kaum Klicks holen.
 *                15 Minuten je Titel, kein Content, kein Risiko.
 * VERSION:       v1.0 · DATUM: 2026-09-07
 * =============================================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from "react-router-dom";
import "./gsc_wochenbericht.css";

const API_BASE = "https://freiseinapp.de/api";

const RANG = {
  1: { t: "🥇 Sofort · Titel und Beschreibung", k: "gw-r1",
       h: "Stehen bereits auf Seite 1 und holen weniger als der Hausdurchschnitt (1,36 % auf Pos 7–10). Es fehlt nicht die Position, sondern der Grund zu klicken." },
  2: { t: "🥈 Dann · FS-Tausch mit Volumen", k: "gw-r2",
       h: "Das Thema stimmt, der Wortlaut trifft die reale Suchanfrage nicht — bei genug Impressionen, dass es sich sofort lohnt." },
  3: { t: "🥉 Danach · FS-Tausch, kleinere Basis", k: "gw-r3",
       h: "Richtig, aber langsamer: diese Seiten stehen weiter hinten. Ein passendes FS wirkt erst, wenn auch die Position kommt." },
  4: { t: "Entscheidung · die Seite steht zur Frage", k: "gw-r4",
       h: "Thema verfehlt. Hier hilft kein FS-Tausch — hier ist zu entscheiden, wofür die Seite da ist." },
  5: { t: "Sichtbarkeit · nah dran", k: "gw-r5",
       h: "Wenig Impressionen, aber Position ≤ 20. Sichtbarkeit geht vor Snippet: interne Links, Index, Inhalt." },
  6: { t: "Beobachten · keine Maßnahme", k: "gw-r6",
       h: "Weder Reichweite noch Position tragen eine Maßnahme. Das ist ein Ergebnis, kein Rest — diese Seiten werden nicht angefasst." },
};

const kurz = u => {
  const s = (u || "").replace("https://verlustangst24.de", "");
  return s === "" || s === "/" ? "/ (Startseite)" : s;
};

/* Die LP-/LB-Nummer steckt im Repo-Pfad, den gsc_kpi_wochen als "datei" fuehrt:
   "Projekte/Themenfuehrerschaft/LB22_VerlustangstLosAnalyse.md" -> "LB22".
   Gemessen 2026-09-08 auf der Woche 2026-09-07: 296 von 296 Zeilen der Ebene
   "seite" tragen sie. Faellt sie doch einmal aus, bleibt der Slug stehen -- eine
   leere Zelle waere schlechter als der lange Name. */
const kennung = d => {
  const m = /\/(L[PB]\d{2})/.exec(d || "");
  return m ? m[1] : null;
};

/* Die Nummer verlinkt auf die Live-Seite.
   🔴 stopPropagation ist Pflicht: die Tabellenzeile oeffnet selbst den Verlauf --
   ohne sie tut ein Klick beides gleichzeitig. */
const Seite = ({ datei, url }) => (
  <a className="gw-seite" href={url} target="_blank" rel="noopener noreferrer"
     title={kurz(url)} onClick={e => e.stopPropagation()}>
    {kennung(datei) || kurz(url)}
  </a>
);
const zahl = (v, n = 0) =>
  v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("de-DE",
    { minimumFractionDigits: n, maximumFractionDigits: n });

/* Delta gegen die Vorwoche — Pfeil und Farbe sagen die Richtung, nicht nur die Zahl. */
function Delta({ jetzt, vorher, umgekehrt = false }) {
  if (vorher === null || vorher === undefined) return null;
  const d = Number(jetzt) - Number(vorher);
  if (!isFinite(d) || d === 0) return <span className="gw-d gw-d-null">±0</span>;
  const gut = umgekehrt ? d < 0 : d > 0;
  return <span className={`gw-d ${gut ? "gw-d-up" : "gw-d-down"}`}>
    {d > 0 ? "▲" : "▼"} {zahl(Math.abs(d), Math.abs(d) < 10 ? 2 : 0)}
  </span>;
}

/* Verlauf einer Seite: die eigentliche Begruendung fuer das Panel.
   Der Markdown-Bericht zeigt eine Woche — hier stehen alle. */
function Verlauf({ token, url, datei, fenster, onClose }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState(null);
  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/gsc_get_wochenbericht.php?fenster=${fenster}&url=${encodeURIComponent(url)}`,
          { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (!weg) { j.success ? setD(j.verlauf) : setFehler(j.message || "Abruf fehlgeschlagen"); }
      } catch (e) { if (!weg) setFehler(String(e)); }
    })();
    return () => { weg = true; };
  }, [token, url, fenster]);

  return (
    <div className="gw-verlauf">
      <div className="gw-verlauf-kopf">
        <strong><Seite datei={datei} url={url} /></strong>
        <span className="gw-hint">{kurz(url)} · Verlauf über alle Wochen · {fenster}-Tage-Fenster</span>
        <button className="gw-x" onClick={onClose}>schließen</button>
      </div>
      {fehler && <p className="gw-fehler">🔴 {fehler}</p>}
      {!d && !fehler && <p className="gw-hint">lädt …</p>}
      {d && d.length === 0 && <p className="gw-hint">Für diese Seite gibt es keine Wochen.</p>}
      {d && d.length > 0 && (
        <table className="gw-tab gw-tab-klein">
          <thead><tr>
            <th>Woche</th><th className="r">Impr</th><th className="r">Kl</th><th className="r">CTR</th>
            <th className="r">Pos</th><th className="r">zugeord.</th><th className="r">Thema fremd</th>
            <th>Stufe</th><th>stärkster Begriff</th>
          </tr></thead>
          <tbody>
            {d.map((v, i) => (
              <tr key={i}>
                <td>{v.woche}</td>
                <td className="r">{zahl(v.k4_impr)}</td>
                <td className="r">{zahl(v.k5_klicks)}</td>
                <td className="r">{zahl(v.k6_ctr, 2)} %</td>
                <td className="r">{zahl(v.position, 1)}</td>
                <td className="r">{zahl(v.q_impr)}</td>
                <td className="r">{v.fremd_anteil === null ? "—" : `${zahl(v.fremd_anteil, 0)} %`}</td>
                <td><span className={`gw-stufe gw-s-${(v.stufe || "").toLowerCase()}`}>{v.stufe || "—"}</span></td>
                <td className="gw-q">{v.top_query || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function GscWochenbericht() {
  const token = localStorage.getItem("token");
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [woche, setWoche] = useState("");
  const [fenster, setFenster] = useState(90);
  const [offen, setOffen] = useState(null);   // URL, deren Verlauf offen ist

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const q = new URLSearchParams({ fenster: String(fenster) });
      if (woche) q.set("woche", woche);
      const r = await fetch(`${API_BASE}/gsc_get_wochenbericht.php?${q}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      j.success ? setD(j) : setFehler(j.message || "Abruf fehlgeschlagen");
    } catch (e) { setFehler(String(e)); }
  }, [token, woche, fenster]);

  useEffect(() => { laden(); }, [laden]);

  if (fehler) return <div className="gw-wrap"><p className="gw-fehler">🔴 {fehler}</p></div>;
  if (!d) return <div className="gw-wrap"><p className="gw-hint">lädt …</p></div>;

  const k = d.kopf || {};
  const v = d.kopf_vorwoche || {};
  const nachRang = {};
  (d.handlungen || []).forEach(h => { (nachRang[h.rang] = nachRang[h.rang] || []).push(h); });

  return (
    <div className="gw-wrap">
      <div className="gw-top">
        <h1>GSC-Wochenbericht</h1>
        <div className="gw-steuer">
          <select value={d.woche} onChange={e => setWoche(e.target.value)}>
            {d.wochen.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          {[7, 30, 60, 90].map(f => (
            <button key={f} className={`gw-f ${f === d.fenster_tage ? "an" : ""}`}
                    onClick={() => setFenster(f)}>{f} T</button>
          ))}
          <Link to="/gsc-status" className="gw-link">🔎 Seiten-Status</Link>
          <Link to="/gsc-kpi" className="gw-link">📊 KPI</Link>
        </div>
      </div>

      {k.fenster_von && <p className="gw-hint">
        Fenster {k.fenster_von} bis {k.fenster_bis} · Vorwoche {d.vorwoche || "—"}
      </p>}
      {d.hinweis && <p className="gw-warn">⚠ {d.hinweis}</p>}

      {/* ── Kennzahlen ─────────────────────────────────────────────── */}
      <div className="gw-karten">
        {[["Impressionen", k.k4_impr, v.k4_impr, 0, false],
          ["Klicks", k.k5_klicks, v.k5_klicks, 0, false],
          ["CTR %", k.k6_ctr, v.k6_ctr, 2, false],
          ["Ø Position", k.position, v.position, 1, true],
          ["FS sichtbar", k.k1_sichtbar, v.k1_sichtbar, 0, false],
          ["auf Seite 1", k.k2_seite1, v.k2_seite1, 0, false]].map(([t, a, b, n, um]) => (
          <div className="gw-karte" key={t}>
            <div className="gw-karte-t">{t}</div>
            <div className="gw-karte-z">{zahl(a, n)}</div>
            <Delta jetzt={a} vorher={b} umgekehrt={um} />
          </div>
        ))}
      </div>

      {/* ── Deutung ── nur wenn eine da ist; sonst faellt der Abschnitt weg ── */}
      {!d.deutung_offen && (
        <section className="gw-abschnitt">
          <h2>Was sich bewegt hat</h2>
          <div className="gw-deutung">{d.deutung}</div>
        </section>
      )}

      {/* ── Handlungen nach Rang ───────────────────────────────────── */}
      <section className="gw-abschnitt">
        <h2>Was zu tun ist</h2>
        <p className="gw-hint">
          Reihenfolge ist <strong>Wirkung je Aufwand</strong>, nicht Größe.
          Klick auf eine Zeile zeigt den Verlauf über alle Wochen.
        </p>
        <div className="gw-tunwrap">
        {Object.keys(nachRang).sort().map(r => (
          <div key={r} className={`gw-rang ${RANG[r]?.k || ""}`}>
            <h3>{RANG[r]?.t || `Rang ${r}`} · {nachRang[r].length} Seiten</h3>
            <p className="gw-rang-h">{RANG[r]?.h}</p>
            {Number(r) >= 6
              ? <p className="gw-liste">{nachRang[r].map((x, i) => <React.Fragment key={i}>{i > 0 && " · "}<Seite datei={x.datei} url={x.url} /></React.Fragment>)}</p>
              : <table className="gw-tab">
                  <thead><tr>
                    <th>Seite</th><th className="r">Impr</th><th className="r">Kl</th>
                    <th className="r">CTR</th><th className="r">Pos</th><th>was zu tun ist</th>
                  </tr></thead>
                  <tbody>
                    {nachRang[r].map((x, i) => (
                      <tr key={i} className="gw-klick" onClick={() => setOffen(offen === x.url ? null : x.url)}>
                        <td><Seite datei={x.datei} url={x.url} /></td>
                        <td className="r">{zahl(x.k4_impr)}</td>
                        <td className="r">{zahl(x.k5_klicks)}</td>
                        <td className="r">{zahl(x.k6_ctr, 2)} %</td>
                        <td className="r">{zahl(x.position, 1)}</td>
                        <td className="gw-grund">{x.rang_grund}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        ))}
        </div>
      </section>

      {offen && <Verlauf token={token} url={offen} datei={(d.seiten || []).find(s => s.url === offen)?.datei} fenster={d.fenster_tage} onClose={() => setOffen(null)} />}

      {/* ── Jede Seite ─────────────────────────────────────────────── */}
      <section className="gw-abschnitt">
        <h2>Jede Seite auf einen Blick · {d.seiten.length}</h2>
        <table className="gw-tab">
          <thead><tr>
            <th>Cluster</th><th>Seite</th><th className="r">Impr</th><th className="r">Kl</th>
            <th className="r">Pos</th><th className="r">zugeord.</th><th className="r">Thema</th>
            <th className="r">Wortlaut</th><th>Stufe</th><th>stärkster Begriff</th>
          </tr></thead>
          <tbody>
            {d.seiten.map((s, i) => (
              <tr key={i} className="gw-klick" onClick={() => setOffen(offen === s.url ? null : s.url)}>
                <td>{s.kategorie}</td>
                <td><Seite datei={s.datei} url={s.url} /></td>
                <td className="r">{zahl(s.k4_impr)}</td>
                <td className="r">{zahl(s.k5_klicks)}</td>
                <td className="r">{zahl(s.position, 1)}</td>
                <td className="r">{zahl(s.q_impr)}</td>
                <td className="r">{s.fremd_anteil === null ? "—" : `${zahl(s.fremd_anteil, 0)} %`}</td>
                <td className="r">{s.fremd_wortlaut === null ? "—" : `${zahl(s.fremd_wortlaut, 0)} %`}</td>
                <td><span className={`gw-stufe gw-s-${(s.stufe || "").toLowerCase()}`}>{s.stufe || "—"}</span></td>
                <td className="gw-q">{s.top_query || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── Datenqualität ──────────────────────────────────────────── */}
      <section className="gw-abschnitt">
        <h2>Datenqualität — wie sicher sind die Zahlen?</h2>
        <p className="gw-hint">🔴 Dieser Abschnitt wird nie weggelassen.</p>
        <table className="gw-tab gw-tab-klein">
          <thead><tr><th>Tabelle</th><th>letzter Tag</th><th className="r">Zeilen</th><th className="r">URLs</th></tr></thead>
          <tbody>
            {Object.entries(d.datenqualitaet || {}).map(([t, q]) => (
              <tr key={t}><td>{t}</td><td>{q.max_datum}</td>
                <td className="r">{zahl(q.zeilen)}</td><td className="r">{zahl(q.urls)}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="gw-warn">
          ⚠ Wo <em>zugeordnet</em> 1 oder 2 lautet, beruht der Prozentsatz auf ein bis zwei
          Einblendungen — dann ist die Zeile ein Hinweis, kein Befund.
        </p>
      </section>
    </div>
  );
}
